import React, { useState, useEffect } from 'react';
import { InstanceWithRole } from '../types/api';
import { apiClient } from '../services/api';
import { X, Loader2, FolderInput } from 'lucide-react';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface MoveToCollectionModalProps {
  categoryId: number;
  currentInstanceId: number | null;
  onSuccess: () => void;
  onClose: () => void;
}

export const MoveToCollectionModal: React.FC<MoveToCollectionModalProps> = ({
  categoryId,
  currentInstanceId,
  onSuccess,
  onClose,
}) => {
  useBodyScrollLock();
  const [instances, setInstances] = useState<InstanceWithRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInstanceId, setSelectedInstanceId] = useState<number | null>(null);
  const [isMoving, setIsMoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchInstances = async () => {
      try {
        const response = await apiClient.getInstances();
        const ownerInstances = response.items.filter(
          (item) =>
            item.user_role === 'owner' && item.instance.id !== currentInstanceId
        );
        setInstances(ownerInstances);
      } catch (err) {
        setError('Failed to load collections. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInstances();
  }, [currentInstanceId]);

  const handleMove = async () => {
    if (selectedInstanceId === null) return;

    try {
      setIsMoving(true);
      await apiClient.updateCategory(categoryId, { instance_id: selectedInstanceId });
      onSuccess();
    } catch (err) {
      setError('Failed to move category. Please try again.');
      setIsMoving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <FolderInput className="h-5 w-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900">Move to Collection</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
            </div>
          ) : error ? (
            <p className="text-sm text-red-600 text-center py-4">{error}</p>
          ) : instances.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              No other collections available to move to.
            </p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {instances.map((item) => (
                <button
                  key={item.instance.id}
                  onClick={() => setSelectedInstanceId(item.instance.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                    selectedInstanceId === item.instance.id
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <p className="font-medium text-gray-900">{item.instance.name}</p>
                  {item.instance.description && (
                    <p className="text-sm text-gray-500 mt-0.5 truncate">
                      {item.instance.description}
                    </p>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end space-x-2 px-4 pb-4">
          <button
            onClick={onClose}
            disabled={isMoving}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleMove}
            disabled={selectedInstanceId === null || isMoving}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isMoving && <Loader2 className="h-4 w-4 animate-spin" />}
            <span>{isMoving ? 'Moving...' : 'Move'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
